import { parse, stringify } from 'querystring';
import { Menu as AntMenu } from 'antd';
import { ParsedQuery } from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { Switcher } from 'components/atoms/switcher';
import { ActionableFilters } from 'components/molecules/filters/actionable-filter';
import { Filter } from 'components/molecules/filters/types';
import { Page } from 'components/molecules/page';
import { FilterContainer } from 'components/organisms/projects-report/common';
import { dateFilterOperationOptions } from 'components/organisms/projects-report/common/filters/filter-fields';
import { CustomRangePicker } from 'components/organisms/projects-report/common/range-picker';
import {
  parseDateRange,
  stringifyDateRange,
} from 'components/organisms/projects-report/common/range-picker/transformer';
import {
  DateRange,
  getRangeObject,
  Range,
} from 'components/organisms/projects-report/common/range-picker/types';
import { UserEditorComponents } from 'components/organisms/tasks/criteria/editor-components';
import { FieldType } from 'components/organisms/tasks/fields';
import { GetSelectEditorComponents } from 'components/organisms/tasks/filter-box/utils';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { TemplateFilter } from 'components/organisms/template-insight';
import { RangePickerTrigger } from 'components/pages/intervals-report/range-picker-trigger';
import { Argon } from 'components/templates';
import { useHash } from 'hooks/use-hash';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useSearch } from 'hooks/use-search';
import {
  useTranslate,
  TranslationKey,
  useTranslation,
} from 'hooks/use-translation';
import { excludedProjectFieldsForFilter } from 'utils/project-fields/constants';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { from_search, to_query } from '../filter-transformer';
import { useTemplateFilters } from '../template-insight/use-template-filters';
import { AnalyzeView } from './analyze-view/view';
import { KeyEventsView } from './key-events-view/view';
import { OverviewView } from './overview-view/view';

export enum ChartZoomLevel {
  Monthly = 'monthly',
  Weekly = 'weekly',
}

export enum IntervalsReportView {
  Overview = 'intervalsOverview',
  KeyEventsOverView = 'keyEventsOverview',
  Analyze = 'analyze',
}

const RangeOptions = [
  Range.Last30Days,
  Range.Last60Days,
  Range.Last90Days,
  Range.Last180Days,
  Range.Last12Months,
  Range.LastMonth,
  Range.LastQuarter,
  Range.ThisMonth,
  Range.ThisQuarter,
  Range.ThisYear,
  Range.Custom,
  Range.Advanced,
];

const Menu = styled(AntMenu)`
  background: var(--coolest-gray-10);
  padding: 0;
  border: 0;
`;

const SwitcherWrapper = styled.div`
  > div > * {
    z-index: 10;
  }
`;

const StyledFilterContainer = styled(FilterContainer)`
  width: 100%;
  justify-content: space-between;
`;

const LeftSectionFiltersWrapper = styled.div`
  display: flex;
`;

export const IntervalInsightPage = () => {
  const { push } = useHistory();
  const [hash, _, getNextHash] = useHash({
    from: (value) => {
      const validViewType = [
        IntervalsReportView.Overview,
        IntervalsReportView.KeyEventsOverView,
        IntervalsReportView.Analyze,
      ];

      if (!validViewType.includes(value.view as IntervalsReportView)) {
        return { view: IntervalsReportView.Overview };
      }

      return value;
    },
    to: (value) => value,
  });

  const view = useMemo(() => hash.view as IntervalsReportView, [hash]);

  const { fields, loading: isFieldsLoading } =
    useProjectFieldsAsFilterableFields();

  const acceptedProjectFields = useMemo(
    () =>
      fields.filter(
        (x) =>
          !excludedProjectFieldsForFilter.includes(x.fieldName) &&
          x.fieldName !== 'partnerCompany',
      ),
    [fields],
  );

  const {
    filter,
    selectFilter,
    clearFilters,
    replaceFilter,
    updateTemplateInFilter,
    templateSelection,
    savedId,
    templateOptions,
    fields_lookup_by_name,
    isTemplateFieldLoading,
    templateField,
  } = useTemplateFilters({ fields: acceptedProjectFields, isFieldsLoading });

  const removeTemplateFromFilter = (options: FieldFilter[]): FieldFilter[] =>
    options.filter((x) => x.field.fieldName !== 'template');

  const onChangeFilter = useCallback(
    (nextFilter: Filter) => {
      const templateFilter = filter.criteria.find(
        (criteria) => criteria.field.fieldName === 'template',
      );
      const { criteria } = nextFilter;

      if (templateFilter) {
        criteria.push(templateFilter);
      }
      selectFilter({
        ...nextFilter,
        criteria,
      });
    },
    [filter.criteria, selectFilter],
  );

  const getSelectEditorComponents: GetSelectEditorComponents = (field) => {
    if (field.fieldName === 'customer') {
      return UserEditorComponents;
    }

    return undefined;
  };
  const [search, setSearch] = useSearch<{ range: DateRange }>({
    from: (value) => {
      const range = parseDateRange(
        (value.range ?? '') as string,
        getRangeObject(Range.Last90Days),
      );

      return { range };
    },
    to: (value) => {
      return { range: stringifyDateRange(value.range) };
    },
  });

  const [viewInitialized, setViewInitialized] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange>(search.range);
  const { search: queryParams } = useLocation();

  useDeepCompareEffect(() => {
    if (viewInitialized) {
      setSelectedRange(search.range);
    }
  }, [search, viewInitialized]);

  const restoreFilterAndRange = useCallback(
    (restore_query: string) => {
      const saved_filter = from_search(fields_lookup_by_name)(restore_query);
      const saved_range = parseDateRange(
        (parse(restore_query).range ?? '') as string,
        getRangeObject(Range.Last90Days),
      );

      setSearch({ range: saved_range }, true);
      replaceFilter(saved_filter);
      setSelectedRange(saved_range);
    },
    [fields_lookup_by_name, replaceFilter, setSearch],
  );

  const [viewSavedQuery, updateViewInLocalStorage] = useLocalStorage(
    'intervals_report.saved_view',
    '',
  );

  useEffect(() => {
    if (!viewInitialized || isFieldsLoading || isTemplateFieldLoading) {
      return;
    }

    updateViewInLocalStorage(
      `${to_query(filter)}&${stringify({
        range: stringifyDateRange(selectedRange),
      })}`,
    );
  }, [
    filter,
    viewInitialized,
    selectedRange,
    viewSavedQuery,
    updateViewInLocalStorage,
    isFieldsLoading,
    isTemplateFieldLoading,
    view,
  ]);

  useEffect(() => {
    if (viewInitialized || isFieldsLoading || isTemplateFieldLoading) {
      return;
    }

    const restore_query =
      (queryParams ?? '') === '' ? viewSavedQuery : queryParams;

    restoreFilterAndRange(restore_query);
    setViewInitialized(true);
  }, [
    isFieldsLoading,
    isTemplateFieldLoading,
    queryParams,
    restoreFilterAndRange,
    viewInitialized,
    viewSavedQuery,
  ]);

  const translate = useTranslate();

  const fromTransformer = (
    value: ParsedQuery<string>,
  ): { zoom: ChartZoomLevel } => {
    const { zoom } = value;

    if (
      Array.isArray(zoom) &&
      Object.values(ChartZoomLevel).includes(zoom[0] as ChartZoomLevel)
    ) {
      return { zoom: zoom[0] as ChartZoomLevel };
    }
    if (
      typeof zoom === 'string' &&
      Object.values(ChartZoomLevel).includes(zoom as ChartZoomLevel)
    ) {
      return { zoom: zoom as ChartZoomLevel };
    }

    return { zoom: ChartZoomLevel.Monthly };
  };

  const tabKeys = Object.values(ChartZoomLevel);
  const [searchTwo, setSearchTwo] = useSearch<{ zoom: ChartZoomLevel }>({
    from: fromTransformer,
    to: (value) => {
      return { zoom: value.zoom };
    },
  });
  const tabs = tabKeys.map((key) => ({
    label: translate(`report.zoom-level.${key}` as unknown as TranslationKey),
    key,
  }));

  const activeTab = searchTwo.zoom;
  const onTabSwitch = (zoom) => {
    setSearchTwo({ zoom });
  };

  const pageTitle = useTranslation('reports.intervals.title');

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Argon
        breadcrumbs={
          <Breadcrumb.Item>
            <Link to="/reports">
              <FormattedMessage id="reports" />
            </Link>
          </Breadcrumb.Item>
        }
        title={<Page.Header.Title>{pageTitle}</Page.Header.Title>}
        tabs={
          <Menu
            mode="horizontal"
            selectedKeys={[view as string]}
            onClick={({ key }) => {
              push({
                search: viewSavedQuery,
                hash: getNextHash({ view: key as string }),
              });
            }}
          >
            <Menu.Item key={IntervalsReportView.Overview}>
              <FormattedMessage id="reports.intervals.intervals_overview" />
            </Menu.Item>
            <Menu.Item key={IntervalsReportView.KeyEventsOverView}>
              <FormattedMessage id="reports.intervals.keyEvents_overview" />
            </Menu.Item>
            <Menu.Item key={IntervalsReportView.Analyze}>
              <FormattedMessage id="reports.intervals.analyze" />
            </Menu.Item>
          </Menu>
        }
        actions={{
          primary: (
            <StyledFilterContainer>
              <LeftSectionFiltersWrapper>
                <ActionableFilters
                  loading={isFieldsLoading}
                  filter={{
                    ...filter,
                    criteria: removeTemplateFromFilter(filter.criteria),
                  }}
                  fields={acceptedProjectFields}
                  onChange={onChangeFilter}
                  reset={clearFilters}
                  getFilterOperationTypeOptions={{
                    [FieldType.Date]: dateFilterOperationOptions,
                  }}
                  getSelectEditorComponents={getSelectEditorComponents}
                />
                <CustomRangePicker
                  range={selectedRange}
                  onDateChange={(range) => setSearch({ range })}
                  triggerComponent={
                    <RangePickerTrigger range={selectedRange} />
                  }
                  rangeOptions={RangeOptions}
                />
                <TemplateFilter
                  templateField={templateField}
                  isLoading={isTemplateFieldLoading}
                  savedId={savedId}
                  selection={templateSelection}
                  options={{
                    ids: [-1, ...templateOptions.ids],
                    items: [
                      {
                        value: -1,
                        label: 'All Templates',
                      },
                      ...templateOptions.items,
                    ],
                  }}
                  updateTemplateInFilter={updateTemplateInFilter}
                />
              </LeftSectionFiltersWrapper>
              <SwitcherWrapper>
                <Switcher
                  value={activeTab}
                  onChange={(e) => onTabSwitch?.(e.target.value)}
                  activeColor="black"
                >
                  {tabs.map(({ key, label }) => (
                    <Switcher.Button key={key} value={key}>
                      {label}
                    </Switcher.Button>
                  ))}
                </Switcher>
              </SwitcherWrapper>
            </StyledFilterContainer>
          ),
          secondary: null,
        }}
        content={
          view === IntervalsReportView.Overview ? (
            <OverviewView
              filter={filter}
              selectedRange={selectedRange}
              viewInitialized={viewInitialized}
              zoomLevel={searchTwo.zoom}
            />
          ) : view === IntervalsReportView.KeyEventsOverView ? (
            <KeyEventsView
              filter={filter}
              selectedRange={selectedRange}
              viewInitialized={viewInitialized}
              zoomLevel={searchTwo.zoom}
            />
          ) : (
            <AnalyzeView
              filter={filter}
              selectedRange={selectedRange}
              viewInitialized={viewInitialized}
              zoomLevel={searchTwo.zoom}
            />
          )
        }
      />
    </>
  );
};
